import * as React from 'react'
import { connect } from 'react-redux'
import * as actions from '../../../actions'
import { reconfigureApi } from '../../../utilities/configureApi'
import { NotifierWrapper, TYPES as NotifierTypes } from '../../atoms/notifier'
import { SlideDecks } from '../../../typeModules/slide'
import LoadingBox from '../../atoms/loading-box'
import ActivationKitSplash from '../../organisms/activation-kit-splash'
import DropshippingStatus from '../../organisms/dropshipping-status'
import InAppEducation from '../../organisms/in-app-education'
import {
  SlideDeck1,
  SlideDeck2,
  SlideDeckVideo,
} from '../../organisms/in-app-education/SlideDecks'
import InstantDashSplash from '../../organisms/instant-dash-splash'
import NeedsVehicleInfo from '../../organisms/needs-vehicle-info'
import RedCard from '../../organisms/redcard'
import PhoneVerify from '../../organisms/phone-verify'
import FastPay from '../../molecules/fast-pay'
import Page from '../../molecules/page'
import PageRoot from '../../molecules/page-root'
import { get } from 'lodash'
import {
  ACTIVATION_KIT_SPLASH_SUB,
  IN_APP_EDUCATION_SUB,
  INSTANT_DASH_SPLASH_SUB,
  RED_CARD,
  FAST_PAY_SUB,
  API_PROXY,
} from '../../../constants'
import { getStore } from '../../../initializers/redux'
import routes from '../../../routes'
import { requestCredentials } from '../../../webview'
import { DasherCredentials } from '../../../webview/types'
import ErrorPage from '../../molecules/error-boundary/error-page'
import EmploymentOffer from '../employment-offer'
import { RiskProvider } from '@doordash/component-risk'
import { ToastProvider } from '@doordash/component-toast'
import LayerManager from '@doordash/component-layer-manager'
import MailingAddress from '../../organisms/mailing-address'
import * as Exp from '../../../experimentConstants'
import * as events from '../../../events'
import i18n from '../../../i18n'
import { WebviewPages } from './constants'

type WebViewProps = {
  sub?: string
  application?: {
    firstName: string
    uniqueLink: string
    mailingAddress: {
      city: string
      postalCode: string
      state: string
      street: string
      streetLine2: string
    }
  }
  applicationStatus: {
    onboardingProgress: {
      hasCompletedFirstDelivery: boolean
    }
  }
  experiments?: any
  loadApplicationWebview?: (any) => Promise<any>
  error?: string | number
  clearErrors?: () => void
  apiStatusCode?: number
  getApplicationStatus?: any
  trackWebviewBootstrapStart: () => void
  trackWebviewRouterResolve: ({ pageName }) => void
  trackWebviewBootstrapFail: ({ error }) => void
}

declare const window
window.requestDasherCredentials = ({
  token,
  uniqueLink,
  deviceId,
}: DasherCredentials) => {
  const store = getStore()
  window.webview.token = token
  window.webview.deviceId = deviceId
  reconfigureApi(token)
  store.dispatch(actions.loadApplicationWebview(uniqueLink))
}

export class WebView extends React.Component<WebViewProps> {
  public static defaultProps = {
    loadApplicationWebview: console.log,
    getApplicationStatus: console.log,
    trackWebviewBootstrapStart: console.log,
    trackWebviewRouterResolve: console.log,
    application: null,
    sub: null,
  }

  public componentDidMount() {
    const {
      loadApplicationWebview,
      trackWebviewBootstrapStart,
      trackWebviewBootstrapFail,
    } = this.props

    trackWebviewBootstrapStart()

    try {
      requestCredentials(async ({ uniqueLink }) => {
        await loadApplicationWebview(uniqueLink)
      })
    } catch (e) {
      trackWebviewBootstrapFail({ error: e })
      throw e
    }
  }

  public router() {
    const {
      application,
      experiments,
      sub,
      applicationStatus,
      trackWebviewRouterResolve,
    } = this.props
    const dasherId = get(application, 'dasherId')
    const uniqueLink = get(application, 'uniqueLink')
    const onboardingProgress = get(applicationStatus, 'onboardingProgress')

    if (experiments[Exp.DX_PHONE_NUMBER_VERIFICATION]) {
      trackWebviewRouterResolve({ pageName: WebviewPages.PhoneVerification })
      return <PhoneVerify />
    }

    if (
      experiments[Exp.DX_SAFETY_COURSE] &&
      !get(onboardingProgress, 'hasCompletedSafetyCourse', true)
    ) {
      trackWebviewRouterResolve({ pageName: WebviewPages.DropshippingStatus })
      return (
        <DropshippingStatus uniqueLink={uniqueLink} application={application} />
      )
    }

    if (experiments[Exp.ADDRESS_COLLECTION_AFTER_X_DASHES]) {
      trackWebviewRouterResolve({ pageName: WebviewPages.MailingAddress })
      return <MailingAddress uniqueLink={uniqueLink} dasherId={dasherId} />
    }

    // these are webview experiences not experiments
    // and should be double checked before reading
    if (
      experiments[Exp.ADDRESS_COLLECTION_AFTER_DASH] &&
      experiments[Exp.INSTANT_DASHING_V2] &&
      experiments[Exp.HAS_COMPLETED_FIRST_DELIVERY] &&
      get(application, 'mailingAddress.street', '') === ''
    ) {
      trackWebviewRouterResolve({ pageName: WebviewPages.MailingAddress })
      return <MailingAddress uniqueLink={uniqueLink} dasherId={dasherId} />
    }

    if (experiments.instant_dashing_v2) {
      if (sub === RED_CARD) {
        trackWebviewRouterResolve({ pageName: WebviewPages.RedCardEntry })
        return (
          <RedCard
            uniqueLink={uniqueLink}
            dasherId={dasherId}
            application={application}
            returnLink={routes.webview.buildPath({
              sub: ACTIVATION_KIT_SPLASH_SUB,
            })}
          />
        )
      }

      if (
        sub === ACTIVATION_KIT_SPLASH_SUB ||
        experiments.has_completed_first_delivery
      ) {
        trackWebviewRouterResolve({
          pageName: WebviewPages.ActivationKitSplash,
        })
        return <ActivationKitSplash />
      }

      if (sub === FAST_PAY_SUB && experiments[Exp.DX_ID_FAST_PAY_Q1_2020]) {
        trackWebviewRouterResolve({ pageName: WebviewPages.FastPaySetup })
        return <FastPay uniqueLink={uniqueLink} />
      }
    }

    if (experiments.dropshipping_status || experiments.bank_onboarding) {
      trackWebviewRouterResolve({ pageName: WebviewPages.DropshippingStatus })
      return (
        <DropshippingStatus uniqueLink={uniqueLink} application={application} />
      )
    }

    trackWebviewRouterResolve({ pageName: WebviewPages.RedCardEntry })
    return (
      <RedCard
        uniqueLink={uniqueLink}
        dasherId={dasherId}
        application={application}
      />
    )
  }

  public render() {
    const { sub, experiments, application } = this.props
    const uniqueLink = get(application, 'uniqueLink')

    const { error, clearErrors, apiStatusCode, trackWebviewRouterResolve } =
      this.props

    // Throws error to trigger fallback UI if an API call
    // dispatches a 401 or 403 status code
    if ([401, 403, 500].includes(apiStatusCode)) {
      trackWebviewRouterResolve({ pageName: WebviewPages.ErrorPage })
      return <ErrorPage />
    }
    if (experiments[Exp.NEEDS_OFFER_LETTER]) {
      return (
        <EmploymentOffer
          uniqueLink={uniqueLink}
          name={get(application, 'firstName')}
        />
      )
    }

    if (sub === INSTANT_DASH_SPLASH_SUB) {
      trackWebviewRouterResolve({ pageName: WebviewPages.InstantDashSplash })
      return <InstantDashSplash />
    }

    // swipe screens
    if (sub === IN_APP_EDUCATION_SUB) {
      let slides = SlideDeck1
      let deck = SlideDecks.SlideDeck1
      if (experiments.in_app_education_v3) {
        slides = SlideDeckVideo
        deck = SlideDecks.SlideDeckVideo
      } else if (experiments.in_app_education_v2) {
        slides = SlideDeck2
        deck = SlideDecks.SlideDeck2
      }
      trackWebviewRouterResolve({ pageName: WebviewPages.InAppEducation })
      return <InAppEducation slides={slides} deck={deck} />
    }

    if (typeof uniqueLink === 'undefined') {
      return <LoadingBox />
    }

    if (experiments[Exp.NEEDS_VEHICLE_INFO]) {
      trackWebviewRouterResolve({ pageName: WebviewPages.VehicleInfoEntry })
      return <NeedsVehicleInfo uniqueLink={uniqueLink} />
    }

    const token = window.webview.token || null
    const baseUrl = window.view.API_URL ? new URL(window.view.API_URL) : null

    return (
      <RiskProvider
        authToken={token}
        language={i18n.getCurrentLocale()}
        proxyOverride={{
          production: {
            baseUrl: baseUrl ? `${baseUrl.protocol}//${baseUrl.host}` : '',
            apiProxy: API_PROXY,
          },
        }}
      >
        <LayerManager>
          <ToastProvider>
            {error && (
              <NotifierWrapper
                message={error}
                onClose={clearErrors}
                type={NotifierTypes.INFO}
                marginTop={'0px'}
              />
            )}
            <Page>
              <PageRoot>{this.router()}</PageRoot>
            </Page>
          </ToastProvider>
        </LayerManager>
      </RiskProvider>
    )
  }
}

const mapStateToProps = (state) => ({
  apiStatusCode: state.apiStatusCode,
  application: state.application,
  error: state.errorMessage,
  experiments: state.experiments,
  applicationStatus: state.applicationStatus.data,
})

const mapDispatchToProps = {
  loadApplicationWebview: actions.loadApplicationWebview,
  getApplicationStatus: actions.getApplicationStatus,
  clearErrors: actions.clearErrors,
  trackWebviewBootstrapStart: events.trackWebviewBootstrapStart,
  trackWebviewRouterResolve: events.trackWebviewRouterResolve,
  trackWebviewBootstrapFail: events.trackWebviewBootstrapFail,
}

export default connect(mapStateToProps, mapDispatchToProps)(WebView)
